import { useCallback, useEffect, useState } from 'react';
import { useLocation } from '@reach/router';

import { LAST_CLOSED_DEFAULT_BANNER_STORAGE_KEY } from './constants';
import { getLastTimeClosedInMs } from './getLastTimeClosedInMs';
import { rememberCloseBannerTime } from './rememberCloseBannerTime';
import { shouldShowBanner } from './shouldShowBanner';
import { shouldShowBannerOnThePage } from './shouldShowBannerOnThePage';

function calculateShouldShowBanner({
  isShowingStarted,
  pathname,
  eligablePathList,
  storageKey,
}: {
  isShowingStarted: boolean;
  pathname: string;
  eligablePathList?: Array<RegExp>;
  storageKey: string;
}): boolean {
  if (!shouldShowBannerOnThePage(pathname, eligablePathList)) {
    return false;
  }
  const currentTimeInMs = Date.now();
  const lastTimeClosedInMs = getLastTimeClosedInMs(storageKey);
  return shouldShowBanner({
    isShowingStarted,
    lastTimeClosedInMs,
    currentTimeInMs,
  });
}

export function useSiteWideBanner({
  isShowingStarted,
  eligablePathList,
  storageKey = LAST_CLOSED_DEFAULT_BANNER_STORAGE_KEY,
}: {
  isShowingStarted: boolean;
  storageKey?: string;
  eligablePathList?: Array<RegExp>;
}) {
  const { pathname, search } = useLocation();

  const pathnameWithSearch = `${pathname}${search}`;

  const bannerState = calculateShouldShowBanner({
    isShowingStarted,
    pathname: pathnameWithSearch,
    eligablePathList,
    storageKey,
  });

  const [isShown, setIsShown] = useState(bannerState);

  useEffect(() => {
    setIsShown(bannerState);
  }, [bannerState, isShowingStarted, pathnameWithSearch]);

  const closeBanner = useCallback(() => {
    const currentTimeInMs = Date.now();
    rememberCloseBannerTime(storageKey, currentTimeInMs);
    setIsShown(false);
  }, [storageKey]);

  return {
    isShown,
    closeBanner,
  };
}
